// 📄 components/shared/ImportarCurlModal.tsx
// Modal de "Importar desde cURL": parsea un comando cURL 100% client-side
// (ver utils/curlParser.ts) y muestra un preview editable antes de rellenar
// el formulario. Dos modos, mismo parser, distinto trato de credenciales:
// - modo="catalogo" (pages/Admin/components/ApiTemplateCard.tsx): las
//   plantillas del catálogo son de referencia, sin secretos — cualquier
//   credencial detectada (API Key, Basic, Bearer) se descarta y se avisa.
// - modo="proyecto" (components/Configuracion/ApiDefinitionCard.tsx): las
//   credenciales son propias de ese proyecto, así que API Key y Basic SÍ
//   se importan (ver curlParser.ts opciones.incluirCredenciales); solo
//   Bearer se descarta siempre por ser un token de un solo uso.
import { useState } from "react";
import { X, ClipboardPaste, AlertTriangle, ShieldAlert, Info } from "lucide-react";
import { parseCurl, ParsedCurlResult, ParsedCredential } from "../../utils/curlParser.ts";

interface ImportarCurlModalProps {
  modo: "catalogo" | "proyecto";
  esAriba: boolean;
  // Cómo repartir un "Authorization: Basic" del cURL — el caller ya sabe
  // el authTipo elegido para esta API (ver ApiDefinitionForm.tsx/
  // ApiTemplateForm.tsx), así que se lo pasa directo en vez de que
  // curlParser.ts adivine por el host (ver comentario en ese archivo).
  authTipoEsperado?: "oauth" | "basic";
  // Solo relevante en modo="proyecto": si ya hay credenciales guardadas,
  // se le pregunta al admin si quiere reemplazarlas antes de confirmar.
  tieneCredencialesGuardadas?: boolean;
  onClose: () => void;
  onImportar: (resultado: ParsedCurlResult) => void;
}

const MENSAJE_CREDENCIAL_CATALOGO: Record<ParsedCredential["tipo"], { nivel: "rojo" | "amarillo"; texto: string }> = {
  apikey: { nivel: "rojo", texto: "Se detectó una API Key — no se importó por seguridad" },
  bearer: { nivel: "amarillo", texto: "Se detectó un token temporal — no se importó, es de un solo uso" },
  basic: { nivel: "rojo", texto: "Se detectaron credenciales Basic — no se importaron por seguridad" }
};

// En modo proyecto, API Key y Basic sí se importan (ver curlParser.ts) —
// el aviso pasa de "no se importó por seguridad" a informativo. Bearer
// se sigue descartando siempre porque es de un solo uso.
const mensajeCredencialProyecto = (tipo: ParsedCredential["tipo"]): { nivel: "amarillo" | "info"; texto: string } => {
  if (tipo === "bearer") return { nivel: "amarillo", texto: "Es un token temporal, no se importó" };
  return { nivel: "info", texto: "Se importaron credenciales del cURL — solo son visibles para este proyecto" };
};

const CLASES_POR_NIVEL: Record<"rojo" | "amarillo" | "info", string> = {
  rojo: "bg-red-500/10 border-red-500/30 text-red-400",
  amarillo: "bg-amber-500/10 border-amber-500/30 text-amber-400",
  info: "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
};

export default function ImportarCurlModal({ modo, esAriba, authTipoEsperado, tieneCredencialesGuardadas, onClose, onImportar }: ImportarCurlModalProps) {
  const [curlText, setCurlText] = useState("");
  const [resultado, setResultado] = useState<ParsedCurlResult | null>(null);
  const [decisionCredenciales, setDecisionCredenciales] = useState<"reemplazar" | "mantener" | null>(null);

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1";

  const handleParsear = () => {
    setResultado(parseCurl(curlText, { incluirCredenciales: modo === "proyecto", authTipoEsperado }));
    setDecisionCredenciales(null);
  };

  const credencialesImportadas = resultado?.ok ? resultado.credencialesImportadas : undefined;
  const hayCredencialesImportadas = !!credencialesImportadas && Object.keys(credencialesImportadas).length > 0;
  const hayConflictoCredenciales = modo === "proyecto" && hayCredencialesImportadas && !!tieneCredencialesGuardadas;
  const confirmarBloqueado = hayConflictoCredenciales && decisionCredenciales === null;

  const handleConfirmar = () => {
    if (!resultado || !resultado.ok || confirmarBloqueado) return;
    if (hayConflictoCredenciales && decisionCredenciales === "mantener") {
      onImportar({ ...resultado, credencialesImportadas: undefined });
      return;
    }
    onImportar(resultado);
  };

  // Filas del preview: solo campos que sí se lograron inferir. "manual"
  // marca las que van resaltadas en amarillo porque el admin las debe
  // completar a mano (Nombre siempre; Data Center/Token URL/Protocolo
  // cuando el parser no pudo inferirlas).
  const filasPlantilla: { label: string; valor?: string; manual: boolean }[] = resultado?.ok
    ? [
        { label: "Nombre de la API", valor: undefined, manual: true },
        { label: "URL Base", valor: resultado.urlBase, manual: false },
        { label: "API Path", valor: resultado.api, manual: false },
        { label: "Protocolo", valor: undefined, manual: true },
        { label: "Data Center", valor: resultado.dataCenter, manual: !resultado.dataCenter },
        ...(esAriba
          ? [
              { label: "Token URL", valor: resultado.tokenUrl, manual: !resultado.tokenUrl },
              { label: "Grant Type", valor: resultado.grantType, manual: !resultado.grantType }
            ]
          : [])
      ]
    : [];

  // Filas de credenciales importadas (solo modo="proyecto"): los valores
  // tipo secreto se muestran enmascarados, igual que sus inputs en
  // ApiDefinitionCard.tsx.
  const filasCredenciales: { label: string; valor: string }[] = credencialesImportadas
    ? [
        ...(credencialesImportadas.apiKey ? [{ label: "API Key", valor: "••••••••" }] : []),
        ...(credencialesImportadas.clientId ? [{ label: "Client ID", valor: credencialesImportadas.clientId }] : []),
        ...(credencialesImportadas.oauthSecret ? [{ label: "OAuth Secret", valor: "••••••••" }] : []),
        ...(credencialesImportadas.usuario ? [{ label: "Usuario", valor: credencialesImportadas.usuario }] : []),
        ...(credencialesImportadas.contraseña ? [{ label: "Contraseña", valor: "••••••••" }] : [])
      ]
    : [];

  const tieneEndpoint = !!resultado?.ok && !!(resultado.metodo && resultado.api);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h3 className="text-lg font-medium text-white flex items-center gap-2">
            <ClipboardPaste className="w-5 h-5 text-indigo-400" /> Importar desde cURL
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className={labelClass}>Comando cURL</label>
            <textarea
              className={`${inputClass} font-mono resize-y`}
              rows={6}
              value={curlText}
              onChange={(e) => setCurlText(e.target.value)}
              placeholder={"Pega aquí tu cURL... Ej: curl --location 'https://eu.openapi.ariba.com/api/...' --header 'APIkey: xxx'"}
            />
          </div>

          {!resultado && (
            <button
              type="button"
              onClick={handleParsear}
              disabled={!curlText.trim()}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Parsear e importar
            </button>
          )}

          {resultado && !resultado.ok && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              {resultado.error}
            </div>
          )}

          {resultado && resultado.ok && (
            <div className="space-y-4">
              {resultado.warnings.map((w) => (
                <div key={w} className="bg-amber-500/10 border border-amber-500/30 text-amber-400 p-3 rounded-lg text-xs flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {w}
                </div>
              ))}

              {resultado.detectedCredentials.map((c, i) => {
                const msg = modo === "catalogo" ? MENSAJE_CREDENCIAL_CATALOGO[c.tipo] : mensajeCredencialProyecto(c.tipo);
                return (
                  <div key={`${c.header}-${i}`} className={`${CLASES_POR_NIVEL[msg.nivel]} border p-3 rounded-lg text-xs flex items-start gap-2`}>
                    {msg.nivel === "info" ? <Info className="w-4 h-4 flex-shrink-0 mt-0.5" /> : <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />}
                    {msg.texto} ({c.header})
                  </div>
                );
              })}

              {hayConflictoCredenciales && (
                <div className="bg-indigo-500/10 border border-indigo-500/30 text-indigo-300 p-3 rounded-lg text-xs space-y-2">
                  <p>Este proyecto ya tiene credenciales guardadas. ¿Deseas reemplazarlas con las del cURL?</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setDecisionCredenciales("reemplazar")}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                        decisionCredenciales === "reemplazar" ? "bg-indigo-600 text-white" : "bg-white/5 text-indigo-300 hover:bg-white/10"
                      }`}
                    >
                      Sí, reemplazar
                    </button>
                    <button
                      type="button"
                      onClick={() => setDecisionCredenciales("mantener")}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                        decisionCredenciales === "mantener" ? "bg-indigo-600 text-white" : "bg-white/5 text-indigo-300 hover:bg-white/10"
                      }`}
                    >
                      Mantener las actuales
                    </button>
                  </div>
                </div>
              )}

              <div className="bg-black/20 border border-white/10 rounded-xl p-4 space-y-2">
                <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wider">Se rellenará en la plantilla</h4>
                <div className="space-y-1.5">
                  {filasPlantilla
                    .filter((f) => f.valor || f.manual)
                    .map((f) => (
                      <div
                        key={f.label}
                        className={`flex items-center justify-between gap-3 text-xs p-2 rounded-lg ${
                          f.manual ? "bg-amber-500/10 border border-amber-500/30" : "bg-white/5"
                        }`}
                      >
                        <span className={f.manual ? "text-amber-400 font-medium" : "text-gray-400"}>{f.label}</span>
                        <span className={`font-mono truncate max-w-[60%] ${f.manual ? "text-amber-300" : "text-white"}`}>
                          {f.valor || "Completar manualmente"}
                        </span>
                      </div>
                    ))}
                  {filasCredenciales.map((f) => (
                    <div key={f.label} className="flex items-center justify-between gap-3 text-xs p-2 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                      <span className="text-emerald-400">{f.label}</span>
                      <span className="font-mono truncate max-w-[60%] text-emerald-300">{f.valor}</span>
                    </div>
                  ))}
                </div>
              </div>

              {tieneEndpoint && (
                <div className="bg-black/20 border border-white/10 rounded-xl p-4 space-y-1.5">
                  <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Se creará 1 endpoint: {resultado.metodo} {resultado.api}
                    {resultado.endpointSugerido ? `/${resultado.endpointSugerido}` : ""}
                  </h4>
                  {(resultado.params?.length ?? 0) > 0 && (
                    <p className="text-[11px] text-gray-500">Params: {resultado.params!.map((p) => `${p.clave}=${p.valor}`).join(", ")}</p>
                  )}
                  {(resultado.headers?.length ?? 0) > 0 && (
                    <p className="text-[11px] text-gray-500">Headers: {resultado.headers!.map((h) => h.clave).join(", ")}</p>
                  )}
                  {(resultado.pathVariablesSugeridas?.length ?? 0) > 0 && (
                    <p className="text-[11px] text-amber-400">
                      Path Variables sugeridas: {resultado.pathVariablesSugeridas!.map((v) => `${v.clave}=${v.valor}`).join(", ")} (revisa el nombre antes de guardar)
                    </p>
                  )}
                  {resultado.bodyEjemplo && (
                    <p className="text-[11px] text-gray-500">Incluye body de ejemplo.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-5 border-t border-white/10">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
          >
            Cancelar
          </button>
          {resultado && resultado.ok && (
            <button
              type="button"
              onClick={handleConfirmar}
              disabled={confirmarBloqueado}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Confirmar importación
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
